<template>
  <AppLoading v-if="isLoading" />
  <div v-else>
    <header>
      <div class="px-3 py-2 border-bottom d-flex align-items-center">
        <AppHeading class="flex-1 d-flex align-items-center">
          <AppIcon
            name="home"
            class="mr-2"
            :size="30"
          />Reporting Dashboard
        </AppHeading>
        <FormTermSelect
          v-model="term"
          :config="{ multiple: false }"
        />
      </div>
      <div
        id="stats"
        class="d-flex border-bottom"
      >
        <div
          v-for="(stat,i) in headerStats"
          :key="i"
          class="border-right flex-1 text-center d-flex flex-column"
        >
          <div class="px-2 py-5 m-2 bg-lighter rounded-5 flex-1">
            <div class="display-3 font-weight-bold">{{ stat.value }}</div>
            <small class="text-muted text-uppercase">{{ stat.label }}</small>
          </div>
        </div>
      </div>
    </header>
    <article>
      <div class="d-flex border-bottom">
        <div class="flex-1 border-right d-flex flex-column">
          <div class="bg-lighter rounded-5 p-3 m-2 flex-1 d-flex flex-column">
            <div class="font-weight-bold mb-4">Growth of student credit hours</div>
            <ReportingLineChart
              class="flex-1"
              :dataset="growthStudentCreditHours.datasets"
              :options="growthStudentCreditHours.options"
            />
          </div>
        </div>
        <div class="flex-1 border-right d-flex flex-column">
          <div class="bg-lighter rounded-5 p-3 m-2 flex-1">
            <div>
              <div class="d-flex align-items-start">
                <div class="flex-1">
                  <div class="font-weight-bold">Attempted Student Credit Hours</div>
                  <small class="text-muted">Trends in Attempted Credit Hours by Course Division and Term</small>
                </div>
                <div class="text-right">
                  <small class="text-muted">3 Yr Trend Rate - Attempted SCH</small>
                  <div class="display-4 text-danger">-1.1%</div>
                </div>
              </div>
            </div>
            <ReportingBarChart
              :dataset="attemptedStudentCreditHours.datasets"
              :options="attemptedStudentCreditHours.options"
            />
          </div>
        </div>
      </div>
      <div class="border-bottom">
        <div>
          <div class="d-flex">
            <div class="border-right flex-1 d-flex flex-column">
              <div class="d-flex flex-1 align-items-center m-2 bg-lighter rounded-5 p-3 align-items-center">
                <div class="font-weight-bold flex-1">Class Capacity Utilization</div>
              </div>
            </div>
            <div class="d-flex align-items-center flex-1 border-right">
              <div class="border-right flex-1 d-flex flex-column">
                <div class="flex-1 text-right m-2 bg-lighter rounded-5 p-3">
                  <small class="text-muted">Median Class Size</small>
                  <div class="display-4">25</div>
                </div>
              </div>
              <div class="border-right flex-1 d-flex flex-column">
                <div class="flex-1 text-right m-2 bg-lighter rounded-5 p-3">
                  <small class="text-muted">Median Class Capacity</small>
                  <div class="display-4">33</div>
                </div>
              </div>
              <div class="flex-1 d-flex flex-column">
                <div class="flex-1 text-right m-2 bg-lighter rounded-5 p-3">
                  <small class="text-muted">Median Class Fill Rate</small>
                  <div class="display-4">63%</div>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex border-top">
            <div class="border-right flex-1 d-flex flex-column">
              <div class="flex-1 p-3 m-2 bg-lighter rounded-5 align-items-center">
                <div class="font-weight-bold mb-4">Trends in Median Class Size and Class Capacity</div>
                <ReportingLineChart
                  :dataset="trendsMedianClassSizeCapacity.datasets"
                  :options="trendsMedianClassSizeCapacity.options"
                />
              </div>
            </div>
            <div class="border-right flex-1 d-flex flex-column">
              <div class="flex-1 p-3 m-2 bg-lighter rounded-5 align-items-center">
                <div class="font-weight-bold mb-4">Median Class Size and Class Capacity by Course Type</div>
                <ReportingBarChart
                  :dataset="medianClassSizeCapacityCourseType.datasets"
                  :options="medianClassSizeCapacityCourseType.options"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="border-bottom">
        <div class="bg-lighter rounded-5 m-2">
          <div class="px-3 pt-3 d-flex">
            <div class="font-weight-bold flex-1">Distribution of Section Fill Rates by Term</div>
          </div>
          <div class="p-3">
            <ReportingBarChart
              :dataset="distributionSectionFillRatesByTerm.datasets"
              :options="distributionSectionFillRatesByTerm.options"
            />
          </div>
        </div>
      </div>
      <div class="border-bottom">
        <div class="d-flex">
          <div class="border-right flex-1 d-flex flex-column">
            <div class="d-flex flex-1 align-items-center m-2 bg-lighter rounded-5 p-3 align-items-center">
              <div class="flex-1">
                <div class="font-weight-bold">Section Consolidation Opportunities - Multiple Section Courses</div>
                <small class="text-muted">These reports exclude courses with one of fewer sections offered per academic year or term.</small>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center flex-1 border-right">
            <div class="flex-1 d-flex flex-column">
              <div class="flex-1 text-right m-2 bg-lighter rounded-5 p-3">
                <small class="text-muted">No. of Collapsible Sections</small>
                <div class="display-4">7</div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex border-top">
          <div class="border-right flex-1 d-flex flex-column">
            <div class="flex-1 p-3 m-2 bg-lighter rounded-5 align-items-center">
              <div class="font-weight-bold mb-4">Trends in Section Consolidation Opportunities (80% Target Fill Rate)</div>
              <ReportingLineChart
                :dataset="trendsSectionConsolidationOpportunities.datasets"
                :options="trendsSectionConsolidationOpportunities.options"
              />
            </div>
          </div>
          <div class="border-right flex-1 d-flex flex-column">
            <div class="flex-1 p-3 m-2 bg-lighter rounded-5 align-items-center">
              <div class="font-weight-bold mb-4">Top Courses With Section Consolidation Opportunity (80% Target Fill Rate)</div>
              <ReportingHorizontalBarChart
                :dataset="topCoursesSectionConsolidationOpportunities.datasets"
                :options="topCoursesSectionConsolidationOpportunities.options"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="border-bottom d-flex">
        <div class="flex-1">
          <div class="d-flex">
            <div class="border-right flex-1 d-flex flex-column">
              <div class="d-flex flex-1 m-2 bg-lighter rounded-5 p-3">
                <div class="flex-1">
                  <div class="font-weight-bold">Course Bottlenecks</div>
                  <small class="text-muted">Student Attribute filters do not apply to the following Course Bottlenecks reports.</small>
                </div>
              </div>
            </div>
            <div class="d-flex flex-column flex-1 border-right">
              <div class="flex-1 d-flex flex-column">
                <div class="flex-1 text-right m-2 bg-lighter rounded-5 p-3">
                  <small class="text-muted">% of Courses w/ FIll Rate > 90%</small>
                  <div class="display-4">6.5%</div>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex border-top">
            <div class="border-right flex-1 d-flex flex-column">
              <div class="flex-1 p-3 m-2 bg-lighter rounded-5 align-items-center">
                <div class="font-weight-bold mb-4">Number of Bottleneck Courses By Course Division (Fill Rate >= 90%)</div>
                <ReportingBarChart
                  :dataset="bottleneckCoursesByCourseDivision.datasets"
                  :options="bottleneckCoursesByCourseDivision.options"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="border-right flex-1 d-flex flex-column">
          <div class="flex-1 p-3 m-2 bg-lighter rounded-5 align-items-center">
            <div class="mb-4">
              <div class="font-weight-bold">Department Growth Planning</div>
              <small class="text-muted d-block">Annual Percent Change in Attempted SCH vs Median Section Fill Rates</small>
            </div>
            <div class="position-relative">
              <ReportingBubbleChart
                :dataset="departmentGrowthPlanning.datasets"
                :options="departmentGrowthPlanning.options"
              />
              <span id="q1">Departments with room to grow</span>
              <span id="q2">Possible capacity constrains</span>
              <span id="q3">Capacity fully utilized</span>
              <span id="q4">Possible resource reallocation</span>
            </div>
          </div>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
import FormTermSelect from '@/components/Forms/FormTermSelect'
import ReportingLineChart from '@/components/Reporting/ReportingLineChart'
import ReportingBarChart from '@/components/Reporting/ReportingBarChart'
import ReportingHorizontalBarChart from '@/components/Reporting/ReportingHorizontalBarChart'
import ReportingBubbleChart from '@/components/Reporting/ReportingBubbleChart'
import { mapGetters } from 'vuex'
import mockData from './MockData'
import { fetchReports } from '@/api/reporting'
import _get from 'lodash/get'

export default {
  components: {
    FormTermSelect,
    ReportingLineChart,
    ReportingBarChart,
    ReportingHorizontalBarChart,
    ReportingBubbleChart,
  },
  data () {
    return {
      term: null,
      growthStudentCreditHours: mockData.growthStudentCreditHours,
      attemptedStudentCreditHours: mockData.attemptedStudentCreditHours,
      trendsMedianClassSizeCapacity: mockData.trendsMedianClassSizeCapacity,
      medianClassSizeCapacityCourseType: mockData.medianClassSizeCapacityCourseType,
      distributionSectionFillRatesByTerm: mockData.distributionSectionFillRatesByTerm,
      trendsSectionConsolidationOpportunities: mockData.trendsSectionConsolidationOpportunities,
      topCoursesSectionConsolidationOpportunities: mockData.topCoursesSectionConsolidationOpportunities,
      bottleneckCoursesByCourseDivision: mockData.bottleneckCoursesByCourseDivision,
      departmentGrowthPlanning: mockData.departmentGrowthPlanning,
      reports: [],
      isLoading: true,
    }
  },
  computed: {
    ...mapGetters([ 'currentTerm', 'school' ]),
    headerStats () {
      const reportMaps = {
        findCurriculumCoursesCount: {
          label: 'Number of courses',
          valuePath: 'data.count',
        },
        findProgramsCount: {
          label: 'Number of programs',
          valuePath: 'data.count',
        },
        findSectionsCountByTerm: {
          label: 'Number of Sections',
          valuePath: 'data.0.count',
        },
        findTotalCreditsGrowth: {
          label: 'SCH 1-year growth',
          valuePath: 'data.growth',
        },
        findTotalCreditsByTerm: {
          label: 'Total attempted SCH',
          valuePath: 'data.0.totalCredits',
        },
      }

      return this.reports.map(report => {
        const reportMap = reportMaps[report.reportName]
        return {
          label: reportMap.label,
          value: _get(report, reportMap.valuePath, 0),
        }
      })
    },
  },
  watch: {
    term: {
      handler: 'fetchReports',
    },
  },
  async created () {
    this.term = this.currentTerm
  },
  methods: {
    async fetchReports () {
      this.isLoading = true
      const reportsFetchConfig = {
        reports: [
          {
            name: 'findProgramsCount',
            params: {},
          },
          {
            name: 'findCurriculumCoursesCount',
            params: {},
          },
          {
            name: 'findTotalCreditsByTerm',
            params: {
              terms: [ this.term.id ],
            },
          },
          {
            name: 'findSectionsCountByTerm',
            params: {
              terms: [ this.term.id ],
            },
          },
          {
            name: 'findTotalCreditsGrowth',
            params: {
              termRange: 'oneYearBackTerm',
              selectedTerm: this.term.id,
            },
          },
        ],
      }

      this.reports = await fetchReports({ school: this.school, ...reportsFetchConfig })
      this.isLoading = false
    },
  },
}
</script>

<style scoped>
.bg-lighter {
  background-color: #f7fbff;
}
#q1,
#q2,
#q3,
#q4 {
  position: absolute;
  width: 150px;
  opacity: 0.75;
}
#q1 {
  top: 10%;
  left: 20%;
}
#q2 {
  top: 10%;
  right: 10%;
}
#q3 {
  top: 70%;
  right: 10%;
}
#q4 {
  top: 70%;
  left: 20%;
}
</style>
